/**
 * Bangladesh-specific savings product presets for the Compound Interest Calculator.
 * Rates are indicative — users must verify with the bank/post office.
 */

import type { CompoundFrequency } from "@/lib/calculators/compoundInterest";

export type ProductKind = "dps" | "fdr" | "sanchayapatra" | "other" | "custom";

export interface SavingsProduct {
  id: string;
  kind: ProductKind;
  name: { en: string; bn: string };
  /** Indicative annual interest rate in % */
  rate: number;
  /** Default tenure in years */
  defaultYears: number;
  defaultFrequency: CompoundFrequency;
  /** Default mode preset (lumpsum, recurring, or both) */
  defaultMode: "lumpsum" | "recurring" | "both";
  /** Default monthly contribution suggestion (0 if N/A) */
  defaultMonthly?: number;
  /** Default principal (lump sum) suggestion */
  defaultPrincipal?: number;
  note?: { en: string; bn: string };
}

export const BD_SAVINGS_PRODUCTS: SavingsProduct[] = [
  {
    id: "custom",
    kind: "custom",
    name: { en: "Custom", bn: "কাস্টম" },
    rate: 8,
    defaultYears: 10,
    defaultFrequency: "annually",
    defaultMode: "lumpsum",
    defaultPrincipal: 100_000,
    defaultMonthly: 5_000,
  },
  {
    id: "dbbl-dps",
    kind: "dps",
    name: { en: "DBBL DPS", bn: "ডিবিবিএল ডিপিএস" },
    rate: 7,
    defaultYears: 10,
    defaultFrequency: "monthly",
    defaultMode: "recurring",
    defaultMonthly: 5_000,
    defaultPrincipal: 0,
  },
  {
    id: "brac-dps",
    kind: "dps",
    name: { en: "BRAC Bank DPS", bn: "ব্র্যাক ব্যাংক ডিপিএস" },
    rate: 7.5,
    defaultYears: 10,
    defaultFrequency: "monthly",
    defaultMode: "recurring",
    defaultMonthly: 5_000,
    defaultPrincipal: 0,
  },
  {
    id: "sonali-dps",
    kind: "dps",
    name: { en: "Sonali Bank DPS", bn: "সোনালী ব্যাংক ডিপিএস" },
    rate: 7.5,
    defaultYears: 10,
    defaultFrequency: "monthly",
    defaultMode: "recurring",
    defaultMonthly: 5_000,
    defaultPrincipal: 0,
  },
  {
    id: "fdr-1y",
    kind: "fdr",
    name: { en: "FDR — 1 Year", bn: "এফডিআর — ১ বছর" },
    rate: 8.5,
    defaultYears: 1,
    defaultFrequency: "semiannually",
    defaultMode: "lumpsum",
    defaultPrincipal: 100_000,
  },
  {
    id: "fdr-3y",
    kind: "fdr",
    name: { en: "FDR — 3 Years", bn: "এফডিআর — ৩ বছর" },
    rate: 9,
    defaultYears: 3,
    defaultFrequency: "semiannually",
    defaultMode: "lumpsum",
    defaultPrincipal: 100_000,
  },
  {
    id: "sp-5y",
    kind: "sanchayapatra",
    name: { en: "5-Year Bangladesh Sanchayapatra", bn: "৫ বছর মেয়াদী বাংলাদেশ সঞ্চয়পত্র" },
    rate: 11.04,
    defaultYears: 5,
    defaultFrequency: "semiannually",
    defaultMode: "lumpsum",
    defaultPrincipal: 100_000,
  },
  {
    id: "sp-family",
    kind: "sanchayapatra",
    name: { en: "Family / Pensioner Sanchayapatra", bn: "ফ্যামিলি / পেনশনার সঞ্চয়পত্র" },
    rate: 11.76,
    defaultYears: 5,
    defaultFrequency: "monthly",
    defaultMode: "lumpsum",
    defaultPrincipal: 100_000,
  },
  {
    id: "us-bond",
    kind: "other",
    name: { en: "US Savings Bond (≈4%)", bn: "ইউএস সেভিংস বন্ড (≈৪%)" },
    rate: 4,
    defaultYears: 10,
    defaultFrequency: "semiannually",
    defaultMode: "lumpsum",
    defaultPrincipal: 100_000,
  },
  {
    id: "mutual-fund",
    kind: "other",
    name: { en: "Mutual Fund (avg 12%)", bn: "মিউচুয়াল ফান্ড (গড় ১২%)" },
    rate: 12,
    defaultYears: 10,
    defaultFrequency: "annually",
    defaultMode: "both",
    defaultPrincipal: 50_000,
    defaultMonthly: 5_000,
  },
];

export const SAVINGS_DISCLAIMER = {
  en: "Indicative rate — verify with bank or post office before investing.",
  bn: "প্রদর্শিত হার আনুমানিক — বিনিয়োগের আগে ব্যাংক বা ডাকঘরের সঙ্গে যাচাই করুন।",
};
